import { Injectable, Injector } from '@angular/core';
import {
  ElementTypeCategoriesDto,
  ProfileSerieDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { DateHelperService } from '@common-modules/shared/helpers/date-helper.service';
import { DimensionTypesEnum } from '@common-modules/shared/model/shared/dimension-types';
import { ChartSerieTypeEnum } from '@common-modules/wlm-charts/core/models/chart-serie-type.enum';
import { GCartesianChartLabel } from '@common-modules/wlm-charts/core/models/generic-chart-settings/g-cartesian-chart-label';
import { GCartesianChartSerie } from '@common-modules/wlm-charts/core/models/generic-chart-settings/g-cartesian-chart-series';
import { GChartSerieDataPoint } from '@common-modules/wlm-charts/core/models/generic-chart-settings/g-chart-serie-data-point';
import { GenericCartesianChartSettings } from '@common-modules/wlm-charts/core/models/generic-chart-settings/generic-cartesian-chart-settings';
import { BaseCartesianChartService } from '@common-modules/wlm-charts/core/services/base-cartesian-chart.service';
import { asEnumerable } from 'linq-es2015';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProfilesService } from '../../services/profiles.service';

@Injectable()
export class ProfileChartService extends BaseCartesianChartService {
  protected NADimension = 50;

  constructor(
    injector: Injector,
    private _dateHelperService: DateHelperService,
    private _profilesService: ProfilesService
  ) {
    super(injector);
  }

  protected get url() {
    return `${this.apiUrl}/attributes`;
  }

  public getData(params: any): Observable<any[]> {
    const queryParams = params.queryParams;

    const categories: ElementTypeCategoriesDto[] = queryParams.attributesMetadata;

    const startDate = new Date(this._dateHelperService.toApiFormat(queryParams.startDate));

    return this._profilesService.getScenarioProfileSeries(queryParams.profileId, startDate).pipe(
      map((profile) => {
        const profileSeriesDto = profile.profileSerieValues;

        const attributesMetadata = asEnumerable(categories)
          .SelectMany((x) =>
            asEnumerable(x.categories).SelectMany((x) => asEnumerable(x.attributes))
          )
          .ToArray();

        const profileSeries: ProfileSerie[] = [];

        profileSeriesDto.forEach((profile) => {
          const attributeMetadata = attributesMetadata.find((x) => x.name == profile.attribute);
          if (attributeMetadata) {
            const profileSerie = new ProfileSerie({
              attribute: profile.attribute,
              values: profile.values,
              unit: attributeMetadata.units,
              name: attributeMetadata.alias,
              physType: attributeMetadata.physType,
            });
            profileSeries.push(profileSerie);
          }
        });

        return profileSeries;
      })
    );
  }

  public mapDataToGenericSettings(data: ProfileSerie[]): Observable<GenericCartesianChartSettings> {
    const series = data.map((profileSerie) => {
      const dataPoints: GChartSerieDataPoint[] = [];

      // This should be already filtered and ordered by pathValue in the API side
      const profileSerieOrderedValues = asEnumerable(profileSerie.values)
        .Where((x) => x.value !== null && x.value !== undefined && x.value !== 0)
        .OrderBy((x) => x.pathValue);

      for (const trendValue of profileSerieOrderedValues) {
        const dataPoint: GChartSerieDataPoint = {
          pointCategory: trendValue.pathValue,
          pointValue: +trendValue.value,
          pointLabel: trendValue.elementName,
        };
        dataPoints.push(dataPoint);
      }

      const unit = profileSerie.unit;

      const label = new GCartesianChartLabel({
        minMargin: 10,
        position: 'top',
        show: true,
      });

      const cartesianSerie: GCartesianChartSerie = {
        name: profileSerie.name,
        type: ChartSerieTypeEnum.Line,
        xAxisIndex: 0,
        yAxisIndex: 0,
        xAxisWLMDimensionTypeId: DimensionTypesEnum.NA,
        yAxisWLMDimensionTypeId: DimensionTypesEnum.NA,
        yAxisWLMUnitTypeIdTo: this.NADimension,
        yAxisWLMUnitTypeIdFrom: this.NADimension,
        yAxisWLMUnitCustomLabel: profileSerie.physType,
        yAxisCustomDimension: profileSerie.physType,
        yAxisName: unit,
        xAxisName: 'Chainage (m)',
        isCategorySerie: true,
        dataPoints,
        label,
      };
      return cartesianSerie;
    });

    const categorySerie = asEnumerable(series).FirstOrDefault((x) => x.isCategorySerie);

    const chartSettings = new GenericCartesianChartSettings({
      series,
      xAxisType: 'value',
      useDataZoom: true,
      categoryAxisTitle: categorySerie?.xAxisName,
    });

    return of(chartSettings);
  }
}

export class ProfileSerie extends ProfileSerieDto {
  name: string;
  unit: string;
  physType: string;

  constructor(init: Partial<ProfileSerie>) {
    super(init);
    Object.assign(this, init);
  }
}
